import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Sparkles } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { BuddyAvatar } from "@/components/buddy/BuddyAvatar";
import { speak } from "@/lib/buddy";

const Ctx = createContext(() => {});
export const useCelebrate = () => useContext(Ctx);
const COLORS = ["#C87D53", "#F2D49B", "#6B9080", "#2C4A3E", "#F6C6A8", "#D9D4E7"];

export const Confetti = () => (
  <div className="fixed inset-0 pointer-events-none overflow-hidden z-[60]" aria-hidden>
    {Array.from({ length: 60 }).map((_, i) => (
      <span key={i} className="confetti-bit" style={{ left: `${(i * 37) % 100}%`, background: COLORS[i % COLORS.length], animationDelay: `${(i % 12) * 0.08}s`, animationDuration: `${1.8 + (i % 5) * 0.3}s` }} />
    ))}
  </div>
);

const message = (c, name) => {
  if (c.kind === "levelup") return [`${c.subject} Level Up!`, `Wow ${name}! You moved up in ${c.subject}. I'm so proud of you!`];
  if (c.ready) return ["Level-Up Challenge unlocked!", `You finished ${c.needed} reviews in ${c.subject}. Ready to show what you've learned?`];
  return ["Review complete!", `Great work, ${name}! That's ${c.reviews_done} of ${c.needed} ${c.subject} reviews toward your Level-Up Challenge.`];
};

export const CelebrationProvider = ({ children }) => {
  const { profile, buddy } = useProfile();
  const [c, setC] = useState(null);
  const celebrate = useCallback((x) => x && setC(x), []);
  const name = profile?.name?.split(" ")[0] || "friend";
  useEffect(() => {
    if (!c) return;
    const [, body] = message(c, name);
    speak(body, buddy?.voice).catch(() => {});
    if (c.kind === "review" && !c.ready) { const t = setTimeout(() => setC(null), 4500); return () => clearTimeout(t); }
  }, [c]); // eslint-disable-line react-hooks/exhaustive-deps
  const [title, body] = c ? message(c, name) : [];
  const big = c && (c.kind === "levelup" || c.ready);
  return (
    <Ctx.Provider value={celebrate}>
      {children}
      {c && (
        <>
          {big && <Confetti />}
          <div className={`fixed z-[61] ${big ? "inset-0 grid place-items-center bg-[#2C4A3E]/40 backdrop-blur-sm" : "bottom-24 right-6"}`} onClick={() => big && setC(null)} data-testid="celebration-overlay" data-kind={c.kind}>
            <div onClick={(e) => e.stopPropagation()} className={`rise rounded-[2rem] bg-[#FDFBF7] border border-[#E6DEC3] shadow-2xl ${big ? "p-8 max-w-sm text-center" : "p-4 max-w-xs flex items-center gap-3"}`}>
              <div className={big ? "mx-auto w-24 h-24" : "w-14 h-14 shrink-0"}><BuddyAvatar buddy={buddy} className="w-full h-full" /></div>
              <div>
                <p className={`font-display text-[#2C4A3E] ${big ? "text-3xl mt-4" : "text-lg"}`} data-testid="celebration-title">{c.kind === "levelup" && <Award className="inline w-6 h-6 text-[#C87D53] mr-1" />}{!big && <Sparkles className="inline w-4 h-4 text-[#C87D53] mr-1" />}{title}</p>
                <p className={`text-stone-600 ${big ? "mt-2" : "text-xs"}`}>{body}</p>
                {c.kind === "levelup" && c.badge && <p className="mt-4 inline-block px-4 py-2 rounded-full bg-[#F2D49B] text-[#5B3A1E] text-sm font-medium" data-testid="celebration-badge">{c.badge.name}</p>}
                {big && (
                  <div className="mt-6 flex justify-center gap-3">
                    {c.ready && c.kind === "review" && <Link to={`/level-up/${c.subject_id}`} onClick={() => setC(null)} data-testid="celebration-challenge-btn" className="px-6 py-3 rounded-full bg-[#C87D53] text-white">Take the challenge</Link>}
                    <button onClick={() => setC(null)} data-testid="celebration-close" className="px-5 py-3 rounded-full border border-[#E6DEC3]">{c.ready && c.kind === "review" ? "Later" : "Yay!"}</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Ctx.Provider>
  );
};
